/*
 * What a paid-media provider may be asked for, once a campaign is selected.
 *
 * A governed campaign the platforms never minted an id for has no provider
 * campaign id, and `resolveCampaignAnalysisLiveCampaignId` correctly returns
 * null for it. The danger is what "null" then meant downstream: every provider
 * read on this page builds its query params conditionally —
 *
 *     if (cid) params.set("campaignId", cid)
 *
 * — so a null campaign filter did not narrow the read, it REMOVED the filter.
 * The request became the advertiser's whole account.
 *
 * For Never Stop Chasing that is a direct cross-campaign leak. Campaign 44 and
 * Campaign 50 are two campaigns under one StackAdapt advertiser (141513):
 * 3330628/3330632 belong to 44, 3342439 belongs to 50. An advertiser-wide
 * delivery read returns all three. Selecting Campaign 50 — the campaign with
 * almost no delivery — and receiving Campaign 44's CTV and Display numbers is
 * exactly the inheritance the reporting-isolation work exists to prevent, and
 * it would arrive labelled as Campaign 50.
 *
 * So null is given its true meaning here:
 *
 *   advertiser   — no campaign is selected. The advertiser IS the question.
 *   campaign     — a campaign is selected and the provider knows it by id.
 *   unresolved   — a campaign is selected and the provider cannot address it.
 *                  The honest answer is "this provider has nothing for this
 *                  campaign", NOT the advertiser's numbers. Fail closed.
 *
 * `unresolved` is not an error state. It is the provider-side reading of the
 * governed READY state the page already has a vocabulary for: approved in the
 * current media plan, waiting on delivery evidence.
 *
 * ONE implementation, byte-identical in Cortex and Quantum.
 */

export const CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_ADVERTISER = "advertiser";
export const CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_CAMPAIGN = "campaign";
export const CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_UNRESOLVED = "unresolved";

const text = (value) => String(value ?? "").trim();

export const resolveCampaignAnalysisProviderScope = ({
  selectedCampaignId = null,
  providerCampaignId = null,
  governedCampaignId = null,
} = {}) => {
  const selection = text(selectedCampaignId);
  if (!selection || selection === "auto") {
    return {
      mode: CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_ADVERTISER,
      providerCampaignId: null,
      governedCampaignId: null,
    };
  }

  const provider = text(providerCampaignId);
  if (provider) {
    return {
      mode: CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_CAMPAIGN,
      providerCampaignId: provider,
      governedCampaignId: text(governedCampaignId) || null,
    };
  }

  return {
    mode: CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_UNRESOLVED,
    providerCampaignId: null,
    governedCampaignId: text(governedCampaignId) || selection,
  };
};

/*
 * Whether a paid-media provider read may be issued at all.
 *
 * False ONLY for `unresolved` — a selected campaign the provider cannot
 * address. Callers must treat false as "no provider evidence", never as an
 * excuse to widen the query.
 */
export const canReadProviderForCampaignAnalysisScope = (scope) =>
  scope?.mode !== CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_UNRESOLVED;

/*
 * The governed campaign identity that stays authoritative regardless of what
 * any provider can address. This is what keeps Campaign 50 scoped to 50 while
 * its provider filter is null: the canonical campaign-scoped reads
 * (reportingCampaignId, run context, approved plan, flight) key on THIS, and
 * they are unaffected by a provider's inability to name the campaign.
 */
export const resolveCampaignAnalysisAuthoritativeCampaignId = (scope) =>
  scope?.mode === CAMPAIGN_ANALYSIS_PROVIDER_SCOPE_ADVERTISER
    ? null
    : scope?.governedCampaignId || null;
